import { supabase } from '../supabase'

/**
 * Private rooms for the endless word games (FAKE OUT / CLOSE ENOUGH).
 *
 * The host's signed-in phone creates the session (that is where the
 * All-Access check lives, and it is the only one), then mints a room
 * around it. Everyone else joins with the code and plays their own
 * seat on their own phone for free — one subscriber opens the table.
 * Rooms are code-only by construction: there is no public flag and no
 * matchmaking path, so nobody can be joined by a stranger.
 *
 * Reads go through word_endless_room rather than realtime: the endless
 * tables have RLS on with zero policies, so a subscription would
 * deliver silence rather than an error, which reads as a bug.
 */

// Bind before casting: supabase-js reads `this.rest` inside rpc(), so
// hoisting the bare method into a const detaches it and throws.
type LooseRpc = (fn: string, args?: Record<string, unknown>) =>
  Promise<{ data: unknown; error: Error | null }>
const rpc = supabase.rpc.bind(supabase) as unknown as LooseRpc

async function call<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await rpc(fn, args)
  if (error) throw error
  return data as T
}

export type WordGame = 'fake_out' | 'close_enough'

export interface WordTvSeat {
  player: number
  name: string
  joined: boolean
  /** Host-skipped: excluded from the next-round gate and the pushes. */
  skipped: boolean
  /** CUMULATIVE across every round of the room. */
  score: number | null
  /**
   * One score per round actually dealt, in play order. Always sums to
   * `score`, so the final screen can show 240 + 600 + 0 = 840 rather
   * than a single opaque total. Absent on older servers.
   */
  round_scores?: number[]
  /** Progress in the CURRENT round only. */
  done: number
  /** CLOSE ENOUGH only: this seat gave up rather than solving it.
   *  Either way the round is over for them. */
  gave_up?: boolean
  guesses?: number
}

export interface WordTvRoom {
  code: string
  game: WordGame
  status: 'lobby' | 'playing' | 'finished' | 'abandoned'
  rounds: number
  current_round: number
  /** The CURRENT round's session — changes when the host advances. */
  session: string
  /** 'party' = every seat races its own board (the default);
   *  'coop' = CLOSE ENOUGH only, one shared board for the table.
   *  Absent from servers that predate co-op. */
  mode?: 'party' | 'coop'
  /** Seat index currently holding the room. Changes when a departing
   *  host hands the room to another subscribed seat. Absent from
   *  servers that predate the hand-off. */
  host_seat?: number | null
  seats: WordTvSeat[]
}

/** Mint a room around a session the caller already started. Idempotent. */
export function hostWordTvRoom(
  game: WordGame, session: string, displayName: string, rounds = 1,
  mode: 'party' | 'coop' = 'party',
): Promise<string> {
  return call<string>('word_endless_host', {
    p_game: game, p_session: session, p_display_name: displayName, p_rounds: rounds,
    p_mode: mode,
  })
}

/** Host only. Deals the next round once every unskipped seat is done. */
export function nextWordRound(code: string): Promise<{ session: string; current_round: number }> {
  return call<{ session: string; current_round: number }>('word_endless_next_round', { p_code: code })
}

/** Host only. A skipped seat stops holding up the next-round gate. */
export function skipWordSeat(code: string, player: number, skipped: boolean): Promise<void> {
  return call<void>('word_endless_skip_seat', {
    p_code: code, p_player: player, p_skipped: skipped,
  })
}

export interface WordTvJoined {
  code: string
  game: WordGame
  session: string
  player: number
  status: string
}

/** Claim a seat by code. No subscription check — guests play free. */
export function joinWordTvRoom(code: string, displayName: string): Promise<WordTvJoined> {
  return call<WordTvJoined>('word_endless_join', {
    p_code: code, p_display_name: displayName,
  })
}

/** Host only: lobby → playing. Fires the "room started" push to every
 *  claimed seat except the host. */
export function startWordTvRoom(code: string): Promise<void> {
  return call<void>('word_endless_start', { p_code: code })
}

/** Everything the big screen renders, in one poll. Null if no such room. */
export function fetchWordTvRoom(code: string): Promise<WordTvRoom | null> {
  return call<WordTvRoom | null>('word_endless_room', { p_code: code })
}

export function finishWordTvRoom(code: string): Promise<void> {
  return call<void>('word_endless_finish', { p_code: code })
}

export function leaveWordTvRoom(code: string): Promise<void> {
  return call<void>('word_endless_leave', { p_code: code })
}

/**
 * Leave by SESSION id rather than code — that is what
 * list_my_active_rooms reports as room_id for word rooms, so the Jump
 * Back In card has the session, not the code.
 */
export async function leaveWordTvRoomBySession(session: string): Promise<void> {
  const { data, error } = await supabase
    .from('word_endless_rooms')
    .select('code')
    .eq('session_id', session)
    .maybeSingle()
  if (error) throw error
  const code = (data as { code?: string } | null)?.code
  if (code) await leaveWordTvRoom(code)
}

/** Resolve a session back to its room, for re-entry from Jump Back In. */
export async function lookupWordTvRoomBySession(
  session: string,
): Promise<{ code: string; game: WordGame } | null> {
  const { data, error } = await supabase
    .from('word_endless_rooms')
    .select('code, game')
    .eq('session_id', session)
    .maybeSingle()
  if (error) throw error
  return (data as { code: string; game: WordGame } | null) ?? null
}
